import { claimDetailsRoute } from '@/app/_routes/claim-details.js';
import { Button, Dialog, DialogSurface } from '@fluentui/react-components';
import { DismissRegular, DocumentArrowUpRegular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import { Claim } from './claim.js';
import { Communication } from './communication.js';
import { Contract } from './contract.js';
import { DocumentViewer } from './document-viewer.js';
import { Payments } from './payments.js';
import { useStyles } from './styles.js';
import { SubmitDocumentsModal } from './submit-documents-modal.js';

const LAYOUT_BREAKPOINT = 1280;

type LayoutMode = 'wide' | 'narrow';

export function ClaimDetails() {
    const styles = useStyles();
    const { claimId } = claimDetailsRoute.useParams();
    const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[] | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [layoutMode, setLayoutMode] = useState<LayoutMode>('wide');

    // Detect layout mode based on window width
    useEffect(() => {
        const checkLayoutMode = () => {
            setLayoutMode(window.innerWidth >= LAYOUT_BREAKPOINT ? 'wide' : 'narrow');
        };

        // Initial check
        checkLayoutMode();

        // Listen for resize events
        window.addEventListener('resize', checkLayoutMode);

        return () => {
            window.removeEventListener('resize', checkLayoutMode);
        };
    }, []);

    // Handle closing the document dialog
    const handleCloseDocumentDialog = () => {
        setSelectedDocumentIds(null);
    };

    // Determine if document dialog should be shown
    const isDocumentDialogOpen =
        layoutMode === 'narrow' && selectedDocumentIds !== null && selectedDocumentIds.length > 0;

    return (
        <div className={styles.root}>
            <div className={styles.pageContainer}>
                <div className={styles.actionBar}>
                    <div />
                    <div className={styles.actionButtons}>
                        <Button
                            appearance="primary"
                            icon={<DocumentArrowUpRegular />}
                            onClick={() => setIsModalOpen(true)}
                        >
                            Unterlagen nachreichen
                        </Button>
                    </div>
                </div>

                <div className={styles.container}>
                    <div className={styles.leftColumn}>
                        <Contract claimId={claimId} />
                        <Claim claimId={claimId} />
                        <Communication claimId={claimId} onDocumentsSelect={setSelectedDocumentIds} />
                        <Payments claimId={claimId} onDocumentsSelect={setSelectedDocumentIds} />
                    </div>

                    {layoutMode === 'wide' && (
                        <div className={styles.rightColumn}>
                            <DocumentViewer documentIds={selectedDocumentIds} />
                        </div>
                    )}
                </div>

                <SubmitDocumentsModal claimId={claimId} open={isModalOpen} onOpenChange={setIsModalOpen} />

                <Dialog
                    open={isDocumentDialogOpen}
                    onOpenChange={(_, data) => {
                        if (!data.open) {
                            handleCloseDocumentDialog();
                        }
                    }}
                >
                    <DialogSurface className={styles.documentDialog} backdrop={{ className: styles.dialogBackdrop }}>
                        <Button
                            appearance="subtle"
                            shape="circular"
                            icon={<DismissRegular />}
                            onClick={handleCloseDocumentDialog}
                            className={styles.dialogCloseButton}
                            aria-label="Schließen"
                        />
                        <DocumentViewer documentIds={selectedDocumentIds} />
                    </DialogSurface>
                </Dialog>
            </div>
        </div>
    );
}
